import ct from 'countries-and-timezones';
import parsePhoneNumber from 'libphonenumber-js';
import countries from 'shared/constants/countries.js';

// Contacts without a phone number start on Nepal, the default market.
export const DEFAULT_COUNTRY_CODE = 'NP';
export const DEFAULT_DIAL_CODE = '+977';

const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Finds the country whose dial code is the longest prefix of the given number.
 * @param {string} phoneNumber - Phone number starting with a `+` dial code.
 * @returns {Object|undefined} The matching country from the countries list.
 */
const findCountryByDialCode = phoneNumber =>
  countries
    .filter(({ dial_code: dialCode }) => phoneNumber.startsWith(dialCode))
    .sort((a, b) => b.dial_code.length - a.dial_code.length)[0];

/**
 * Dial code of a country, looked up by its ISO code.
 * @param {string} countryCode - ISO country code, e.g. `NP`.
 * @returns {string} Dial code including the `+`, e.g. `+977`.
 */
export const getDialCodeByCountryCode = countryCode =>
  countries.find(({ id }) => id === countryCode)?.dial_code || '';

export const getActiveCountryCode = () =>
  ct.getCountryForTimezone(getTimezone())?.id || DEFAULT_COUNTRY_CODE;

// The dial code is derived from the resolved country so the flag and the dial
// code can never disagree, and so deprecated timezone aliases (macOS reports
// `Asia/Katmandu`) still resolve to a dial code.
export const getActiveDialCode = () =>
  getDialCodeByCountryCode(getActiveCountryCode()) || DEFAULT_DIAL_CODE;

/**
 * Resolves the country carried by a phone number's `+` prefix. Falls back to a
 * dial code prefix match when libphonenumber cannot parse the number, so
 * partial or unusual numbers still keep their country.
 * @param {string} value - Phone number, e.g. `+9779812345678`.
 * @returns {{countryCode: string, dialCode: string, nationalNumber: string}|null}
 */
export const parseCountryFromPhoneNumber = value => {
  const phoneNumber = String(value ?? '').trim();
  if (!phoneNumber.startsWith('+')) return null;

  const parsed = parsePhoneNumber(phoneNumber);
  const matchedCountry = findCountryByDialCode(phoneNumber);
  const dialCode = parsed?.countryCallingCode
    ? `+${parsed.countryCallingCode}`
    : matchedCountry?.dial_code;

  if (!dialCode) return null;

  return {
    countryCode: parsed?.country || matchedCountry?.id || '',
    dialCode,
    nationalNumber: phoneNumber.slice(dialCode.length),
  };
};
